import * as React from 'react';
import { isNodeSelection, Editor } from '@tiptap/react';

// --- Hooks ---
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from 'containers/Tiptap/components/tiptap-icons/chevron-down-icon';
import { ListIcon } from 'containers/Tiptap/components/tiptap-icons/list-icon';

// --- Lib ---
import { isNodeInSchema } from 'containers/Tiptap/lib/tiptap-utils';

// --- Tiptap UI ---
import {
  ListButton,
  canToggleList,
  isListActive,
  listOptions,
  type ListType,
} from 'containers/Tiptap/components/tiptap-ui/list-button/list-button';

// --- UI Primitives ---
import { ButtonProps, Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
} from 'containers/Tiptap/components/tiptap-ui-primitive/dropdown-menu';

export interface ListDropdownMenuProps extends Omit<ButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The list types to display in the dropdown.
   */
  types?: ListType[];
  /**
   * Whether the dropdown should be hidden when no list types are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function canToggleAnyList(editor: Editor | null, listTypes: ListType[]): boolean {
  if (!editor) return false;
  return listTypes.some((type) => canToggleList(editor, type));
}

export function isAnyListActive(editor: Editor | null, listTypes: ListType[]): boolean {
  if (!editor) return false;
  return listTypes.some((type) => isListActive(editor, type));
}

export function getFilteredListOptions(availableTypes: ListType[]): typeof listOptions {
  return listOptions.filter((option) => !option.type || availableTypes.includes(option.type));
}

export function shouldShowListDropdown(params: {
  editor: Editor | null;
  listTypes: ListType[];
  hideWhenUnavailable: boolean;
  listInSchema: boolean;
  canToggleAny: boolean;
}): boolean {
  const { editor, hideWhenUnavailable, listInSchema, canToggleAny } = params;

  if (!listInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (isNodeSelection(editor.state.selection) || !canToggleAny) {
      return false;
    }
  }

  return true;
}

export function useListDropdownState(
  editor: Editor | null,
  availableTypes: ListType[]
): {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  listInSchema: boolean;
  filteredLists: typeof listOptions;
  canToggleAny: boolean;
  isAnyActive: boolean;
  handleOpenChange: (open: boolean, callback?: (isOpen: boolean) => void) => void;
} {
  const [isOpen, setIsOpen] = React.useState(false);

  // Only offer list types the active editor actually has. The Pages header/footer
  // overlay editor (ConvertKit) has bullet/ordered lists but no taskList, and
  // toggling a node that isn't in the schema throws.
  const supportedTypes = React.useMemo(
    () => availableTypes.filter((type) => isNodeInSchema(type, editor)),
    [availableTypes, editor]
  );

  const listInSchema = supportedTypes.length > 0;

  const filteredLists = React.useMemo(() => getFilteredListOptions(supportedTypes), [supportedTypes]);

  const canToggleAny = canToggleAnyList(editor, supportedTypes);
  const isAnyActive = isAnyListActive(editor, supportedTypes);

  const handleOpenChange = React.useCallback((open: boolean, callback?: (isOpen: boolean) => void) => {
    setIsOpen(open);
    callback?.(open);
  }, []);

  return {
    isOpen,
    setIsOpen,
    listInSchema,
    filteredLists,
    canToggleAny,
    isAnyActive,
    handleOpenChange,
  };
}

export function useActiveListIcon(editor: Editor | null, filteredLists: typeof listOptions): () => React.ReactNode {
  return React.useCallback(() => {
    const activeOption = filteredLists.find((option) => isListActive(editor, option.type));

    return activeOption ? (
      <activeOption.icon className="tiptap-button-icon" />
    ) : (
      <ListIcon className="tiptap-button-icon" />
    );
  }, [editor, filteredLists]);
}

export function ListDropdownMenu({
  editor: providedEditor,
  types = ['bulletList', 'orderedList', 'taskList'],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: ListDropdownMenuProps): React.ReactNode {
  const { editor } = useTiptapEditor(providedEditor);

  const { isOpen, listInSchema, filteredLists, canToggleAny, isAnyActive, handleOpenChange } = useListDropdownState(
    editor,
    types
  );

  const getActiveIcon = useActiveListIcon(editor, filteredLists);

  const show = React.useMemo(
    () =>
      shouldShowListDropdown({
        editor,
        listTypes: types,
        hideWhenUnavailable,
        listInSchema,
        canToggleAny,
      }),
    [editor, types, hideWhenUnavailable, listInSchema, canToggleAny]
  );

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange]
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-active-state={isAnyActive ? 'on' : 'off'}
          role="button"
          tabIndex={-1}
          aria-label="List options"
          tooltip="List"
          {...props}
        >
          {getActiveIcon()}
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {filteredLists.map((option) => (
            <DropdownMenuItem key={option.type} asChild>
              <ListButton
                editor={editor}
                type={option.type}
                text={option.label}
                hideWhenUnavailable={hideWhenUnavailable}
                tooltip=""
              />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ListDropdownMenu;
